import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { ThemeToggle } from '../components/ThemeToggle';
import { Wordmark } from '../components/Wordmark';
import { SpinnerIcon } from '../components/icons';
import { listSsoProviders, startSso, type SsoProviderSummary } from '../features/ssoApi';
import { useThemeContext } from '../theme/useThemeContext';
import { ApiError } from './apiClient';
import { useAuth } from './useAuth';

/** Which step of the sign-in flow is on screen. */
type Stage = 'credentials' | 'mfa';

/** Derived, presentable form state after a failed submit. */
interface FormError {
  /** Banner message shown above the form. */
  message: string;
  /** Severity of the banner. */
  variant: 'critical' | 'warning';
  /** Field-level error for the email input, when the API supplies one. */
  emailError?: string;
  /** Field-level error for the MFA code input, when the API supplies one. */
  mfaCodeError?: string;
}

/** Turn an unknown thrown value into a presentable {@link FormError}. */
function toFormError(error: unknown): FormError {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      const wait = error.retryAfterSeconds;
      return {
        variant: 'warning',
        message:
          wait !== null
            ? `Too many attempts. Please wait ${wait} second${wait === 1 ? '' : 's'} and try again.`
            : 'Too many attempts. Please wait a moment and try again.',
      };
    }
    if (error.status === 403) {
      return {
        variant: 'critical',
        message:
          error.problem?.detail ??
          'This account is not active. Contact an administrator for help.',
      };
    }
    if (error.status === 422) {
      const emailError = error.fieldError('email');
      const mfaCodeError = error.fieldError('mfa_code');
      return {
        variant: 'critical',
        message: error.problem?.detail ?? 'We could not sign you in. Check your details and try again.',
        ...(emailError !== undefined ? { emailError } : {}),
        ...(mfaCodeError !== undefined ? { mfaCodeError } : {}),
      };
    }
    return {
      variant: 'critical',
      message: error.problem?.detail ?? error.message,
    };
  }
  return { variant: 'critical', message: 'Something went wrong. Please try again.' };
}

/**
 * Branded, accessible sign-in screen (spec 08 §7, spec 06 §2.1).
 *
 * Two-step flow. Step one collects email + password. If the account has MFA
 * enrolled the API answers the correct password with an `mfa_required` challenge
 * (a 401 carrying that flag, surfaced by {@link useAuth}'s `login` as a
 * `mfa_required` result rather than an error); the form then advances to step two
 * and asks for a second-factor code, resubmitting the *same* credentials plus the
 * code to complete sign-in. Field- and form-level errors come from RFC-9457
 * problem documents; the throttle message is shown on a 429. On success the auth
 * session flips to authenticated and the public-only route guard redirects in.
 */
export function LoginPage(): JSX.Element {
  const { login, sendEmailOtp } = useAuth();
  const { mode, cycleMode } = useThemeContext();
  const location = useLocation();
  // One-off success notice passed via router state (e.g. after a password reset
  // redirects here). Shown once above the credentials form; not persisted.
  const notice =
    typeof (location.state as { notice?: unknown } | null)?.notice === 'string'
      ? (location.state as { notice: string }).notice
      : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<Stage>('credentials');
  // The account's active second factor, learned from the `mfa_required` challenge.
  // `email_otp` switches the step to the "we emailed you a code" experience; any
  // other value (or none) keeps the authenticator-app prompt.
  const [mfaMethod, setMfaMethod] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // True once we have (re)sent an email code, so the UI can confirm delivery.
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [formError, setFormError] = useState<FormError | null>(null);
  // Ensures the initial email code is auto-sent exactly once per challenge, even
  // if the effect re-runs; reset when returning to the credentials step.
  const autoSentRef = useRef(false);

  const isEmailChallenge = stage === 'mfa' && mfaMethod === 'email_otp';
  // Optional SSO providers (item 7.7): only the ones an admin has enabled and
  // fully configured are returned, so a button never appears unless it will work.
  const [ssoProviders, setSsoProviders] = useState<SsoProviderSummary[]>([]);
  const [ssoBusy, setSsoBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listSsoProviders()
      .then((providers) => {
        if (active) {
          setSsoProviders(providers);
        }
      })
      .catch(() => {
        // SSO is optional; if the listing is unavailable, just hide the buttons.
        if (active) {
          setSsoProviders([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Request (or re-request) a login-time email code for an email-OTP account.
  // The credentials are held in state from step one, so the resend is a single
  // click. Errors (notably a 429 throttle) surface in the form banner.
  const sendEmailChallenge = useCallback(async (): Promise<void> => {
    setResending(true);
    setFormError(null);
    try {
      await sendEmailOtp(email, password);
      setEmailCodeSent(true);
    } catch (error) {
      setFormError(toFormError(error));
    } finally {
      setResending(false);
    }
  }, [email, password, sendEmailOtp]);

  // On entering the email challenge, mail the first code automatically so the
  // user does not have to ask for it. Guarded so it fires once per challenge.
  useEffect(() => {
    if (isEmailChallenge && !autoSentRef.current) {
      autoSentRef.current = true;
      void sendEmailChallenge();
    }
  }, [isEmailChallenge, sendEmailChallenge]);

  const handleCredentialsSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const result = await login(email, password);
      if (result.status === 'mfa_required') {
        // Password accepted; a second factor is needed. Advance to step two —
        // the credentials stay in state so we can resubmit them with the code.
        // The method drives the challenge UI and the auto-send effect.
        setMfaMethod(result.method ?? null);
        setStage('mfa');
      }
    } catch (error) {
      setFormError(toFormError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      // Resubmit the same credentials plus the code. On success the session flips
      // to authenticated and the route guard redirects away from /login.
      await login(email, password, mfaCode.trim());
    } catch (error) {
      setFormError(toFormError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSso = async (provider: string): Promise<void> => {
    setSsoBusy(true);
    setFormError(null);
    try {
      // Hands the browser off to the provider; on success it never returns here.
      await startSso(provider);
    } catch (error) {
      setFormError(toFormError(error));
      setSsoBusy(false);
    }
  };

  const backToCredentials = (): void => {
    setStage('credentials');
    setMfaCode('');
    setPassword('');
    setMfaMethod(null);
    setEmailCodeSent(false);
    autoSentRef.current = false;
    setFormError(null);
  };

  return (
    <div className="flex min-h-full flex-col bg-slate-50 text-slate-900 dark:bg-navy-bg dark:text-slate-100">
      <header className="flex items-center justify-between px-4 py-4 sm:px-6">
        <Wordmark />
        <ThemeToggle mode={mode} onCycle={cycleMode} />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold tracking-tight">
              {stage === 'credentials' ? 'Sign in to DeployHub' : 'Two-step verification'}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {stage === 'credentials'
                ? 'Application lifecycle & deployment management.'
                : isEmailChallenge
                  ? 'Enter the code we emailed you to finish signing in.'
                  : 'Enter the code from your authenticator app to finish signing in.'}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-navy-border dark:bg-navy-surface">
            {notice !== null && formError === null && stage === 'credentials' ? (
              <Alert variant="success" title="Success" className="mb-5">
                {notice}
              </Alert>
            ) : null}

            {formError ? (
              <Alert
                variant={formError.variant}
                title={stage === 'credentials' ? 'Unable to sign in' : 'Verification failed'}
                className="mb-5"
              >
                {formError.message}
              </Alert>
            ) : null}

            {stage === 'credentials' ? (
              <form
                onSubmit={(event) => void handleCredentialsSubmit(event)}
                noValidate
                className="space-y-4"
              >
                <Input
                  label="Email address"
                  type="email"
                  name="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={submitting}
                  error={formError?.emailError}
                />
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={submitting}
                />
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <SpinnerIcon size={16} className="animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={(event) => void handleMfaSubmit(event)} noValidate className="space-y-4">
                {isEmailChallenge ? (
                  <Alert
                    variant="info"
                    title={emailCodeSent ? 'We emailed you a code' : 'Sending your code…'}
                  >
                    {emailCodeSent
                      ? `We sent a 6-digit code to ${email}. It expires in a few minutes.`
                      : 'Hang tight while we email your verification code.'}
                  </Alert>
                ) : null}
                <Input
                  label="Authentication code"
                  type="text"
                  name="mfa_code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value)}
                  disabled={submitting}
                  hint={
                    isEmailChallenge
                      ? 'Enter the 6-digit code we emailed you, or one of your recovery codes.'
                      : 'Enter the 6-digit code from your authenticator app, or one of your recovery codes.'
                  }
                  error={formError?.mfaCodeError}
                />
                <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <SpinnerIcon size={16} className="animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    'Verify and sign in'
                  )}
                </Button>
                {isEmailChallenge ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    disabled={submitting || resending}
                    onClick={() => void sendEmailChallenge()}
                  >
                    {resending ? (
                      <>
                        <SpinnerIcon size={16} className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Resend code'
                    )}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={submitting}
                  onClick={backToCredentials}
                >
                  Back
                </Button>
              </form>
            )}

            {stage === 'credentials' && ssoProviders.length > 0 ? (
              <div className="mt-6">
                <div className="relative mb-4 flex items-center">
                  <span className="h-px flex-1 bg-slate-200 dark:bg-navy-border" />
                  <span className="px-3 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    or
                  </span>
                  <span className="h-px flex-1 bg-slate-200 dark:bg-navy-border" />
                </div>
                <div className="space-y-2">
                  {ssoProviders.map((provider) => (
                    <Button
                      key={provider.provider}
                      type="button"
                      variant="secondary"
                      className="w-full"
                      disabled={ssoBusy || submitting}
                      onClick={() => void handleSso(provider.provider)}
                    >
                      {ssoBusy ? (
                        <SpinnerIcon size={16} className="animate-spin" />
                      ) : null}
                      Continue with {provider.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
            © Unitech Holdings
          </p>
        </div>
      </main>
    </div>
  );
}
